import React, { useState } from 'react';
import { motion } from 'framer-motion';

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const projects = [
    {
      id: 1,
      title: 'E-Commerce Platform',
      category: 'web',
      image: 'https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      description: 'A modern e-commerce platform with advanced product filtering and secure payment processing.',
    },
    {
      id: 2,
      title: 'Health & Fitness App',
      category: 'mobile',
      image: 'https://images.pexels.com/photos/4498362/pexels-photo-4498362.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      description: 'A comprehensive health and fitness app with workout tracking and nutrition planning features.',
    },
    {
      id: 3,
      title: 'Financial Management System',
      category: 'enterprise',
      image: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      description: 'An enterprise-grade financial management system with advanced reporting and analytics.',
    },
    {
      id: 4,
      title: 'Real Estate Platform',
      category: 'web',
      image: 'https://images.pexels.com/photos/186077/pexels-photo-186077.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      description: 'A property listing and management platform with interactive maps and virtual tours.',
    },
    {
      id: 5,
      title: 'Food Delivery App',
      category: 'mobile',
      image: 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      description: 'A user-friendly food delivery app with real-time order tracking and restaurant management.',
    },
    {
      id: 6,
      title: 'Inventory Management System',
      category: 'enterprise',
      image: 'https://images.pexels.com/photos/4481259/pexels-photo-4481259.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      description: 'A comprehensive inventory management system with barcode scanning and automated reordering.',
    },
  ];

  const filters = [
    { id: 'all', label: 'All Projects' },
    { id: 'web', label: 'Web Apps' },
    { id: 'mobile', label: 'Mobile Apps' },
    { id: 'enterprise', label: 'Enterprise Solutions' },
  ];

  const filteredProjects = activeFilter === 'all'
    ? projects
    : projects.filter(project => project.category === activeFilter);

  return (
    <section id="portfolio" className="section bg-secondary-50">
      <div className="container">
        <div className="mb-12 text-center">
          <h2 className="section-title">Our Portfolio</h2>
          <p className="max-w-2xl mx-auto text-lg text-secondary-600">
            Explore our recent projects and discover how we've helped businesses achieve their goals.
          </p>

          <div className="flex flex-wrap justify-center gap-2 mt-8">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeFilter === filter.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-secondary-700 hover:bg-primary-50'
                  }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <motion.div
          className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3"
          layout
        >
          {filteredProjects.map((project) => (
            <motion.div
              key={project.id}
              className="card-3d overflow-hidden bg-white rounded-lg shadow-lg"
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3, type: "spring", stiffness: 100 }}
              whileHover={{
                y: -10,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
              }}
            >
              <div className="relative overflow-hidden h-60 group">
                <motion.img
                  src={project.image}
                  alt={project.title}
                  className="object-cover w-full h-full"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.5 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <motion.div 
                    className="p-4"
                    initial={{ y: 20, opacity: 0 }}
                    whileHover={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <span className="px-3 py-1 text-xs font-medium text-white bg-primary-600/80 backdrop-blur-sm rounded-full shadow-md">
                      {filters.find(f => f.id === project.category)?.label.replace(' Apps', '').replace(' Solutions', '')}
                    </span>
                  </motion.div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="mb-2 text-xl font-bold letter-spacing-tight">{project.title}</h3>
                <p className="mb-4 text-secondary-600 text-pretty">{project.description}</p>
                <div className="flex justify-end">
                  <motion.button
                    className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    whileHover={{ x: 3, color: "#0284c7" }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    View Details
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Portfolio;
